import { orgServiceApiSlice } from "state/services/orgServiceApiSlice";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseFilenameFromContentDisposition = (headerValue) => {
  if (!headerValue) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(headerValue);
  return match ? decodeURIComponent(match[1]) : null;
};

// Injects Copilot AI Credit usage endpoints into the org service API slice.
const copilotApiQueries = orgServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Fetches usage detail records and a summary for a given org and date range.
    // GET /api/organizations/copilot-usage?org_id=&start_date=&end_date=
    getCopilotUsage: builder.query({
      query: ({ orgId, startDate, endDate }) => ({
        url: `/organizations/copilot-usage?org_id=${encodeURIComponent(orgId)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      }),
    }),

    // Fetches the rolling 12-month usage summary for a given org.
    // GET /api/organizations/copilot-usage/monthly?org_id=
    getCopilotUsageMonthly: builder.query({
      query: ({ orgId }) => ({
        url: `/organizations/copilot-usage/monthly?org_id=${encodeURIComponent(orgId)}`,
      }),
      transformResponse: (responseData) => {
        const monthly = (responseData.monthly || []).map((item) => ({
          ...item,
          label: `${MONTH_NAMES[item.month - 1]} ${item.year}`,
        }));
        return { monthly };
      },
    }),

    // Fetches org-level Copilot plan/seat/policy info.
    // GET /api/organizations/copilot-info?org_id=
    getCopilotInfo: builder.query({
      query: ({ orgId }) => ({
        url: `/organizations/copilot-info?org_id=${encodeURIComponent(orgId)}`,
      }),
    }),

    // API 1 — Org-wide (or cost-center-scoped) paginated user Copilot usage table.
    // Exactly one of org_id/cost_center_id is sent — cost_center_id wins when set,
    // so selecting a cost center shows that cost center's users everywhere, not
    // just within the org the dashboard happens to be viewing.
    // GET /api/users/copilot/usage?org_id=|cost_center_id=&from_date=&to_date=&platform_id=&search=&sort_by=&sort_order=&page=&per_page=
    getCopilotUsers: builder.query({
      query: ({ orgId, costCenterId, platformId, fromDate, toDate, search = "", sortBy = "total_consumed_credits", sortOrder = "desc", page = 1, perPage = 20 }) => {
        let url = "/users/copilot/usage?";
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}&page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url };
      },
    }),

    // API 2 — Day-by-day Copilot usage detail for a specific user (org-wide or
    // cost-center-scoped — exactly one of org_id/cost_center_id, same rule as above).
    // GET /api/users/{email}/copilot/usage?org_id=|cost_center_id=&from_date=&to_date=&platform_id=
    getCopilotUserDetail: builder.query({
      query: ({ email, orgId, costCenterId, platformId, fromDate, toDate }) => {
        let url = `/users/${encodeURIComponent(email)}/copilot/usage?`;
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}`;
        return { url };
      },
    }),

    // API 3 — All users who used a specific AI model (org-wide or cost-center-scoped,
    // same exactly-one-of rule).
    // GET /api/users/copilot/models/users?model_name=&org_id=|cost_center_id=&from_date=&to_date=&platform_id=&search=&sort_by=&sort_order=&page=&per_page=
    getCopilotModelUsers: builder.query({
      query: ({ modelName, orgId, costCenterId, platformId, fromDate, toDate, search = "", sortBy = "gross_quantity", sortOrder = "desc", page = 1, perPage = 20 }) => {
        let url = `/users/copilot/models/users?model_name=${encodeURIComponent(modelName)}`;
        url += costCenterId ? `&cost_center_id=${encodeURIComponent(costCenterId)}` : `&org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}&page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url };
      },
    }),

    // API 4 — Export full usage data as Excel (.xlsx). Exactly one of
    // org_id/cost_center_id, same rule as the other user-scoped endpoints —
    // when cost_center_id is passed, the export is scoped to that cost center
    // and the response filename uses the cost center name.
    // GET /api/users/copilot/export?org_id=|cost_center_id=&from_date=&to_date=&platform_id=
    getCopilotExport: builder.query({
      query: ({ orgId, costCenterId, platformId, fromDate, toDate }) => {
        let url = "/users/copilot/export?";
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}`;
        return {
          url,
          responseHandler: (response) => response.blob(),
          cache: "no-cache",
        };
      },
    }),

    // ── Async export jobs (usage) — replaces the synchronous getCopilotExport
    // flow above for large ranges: kick off a job, poll its status until it
    // completes, then fetch a short-lived signed download URL.

    // Kicks off an async usage export job. Exactly one of org_id/cost_center_id,
    // same rule as getCopilotExport above.
    // POST /api/users/copilot/export/jobs?org_id=|cost_center_id=&from_date=&to_date=&platform_id=
    startCopilotExportJob: builder.mutation({
      query: ({ orgId, costCenterId, platformId, fromDate, toDate }) => {
        let url = "/users/copilot/export/jobs?";
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}`;
        return { url, method: "POST" };
      },
    }),

    // Polls the status/progress of a usage export job.
    // GET /api/users/copilot/export/jobs/{job_id}
    getCopilotExportJobStatus: builder.query({
      query: ({ jobId }) => ({
        url: `/users/copilot/export/jobs/${encodeURIComponent(jobId)}`,
        cache: "no-cache",
      }),
    }),

    // Fetches the signed download URL for a completed usage export job.
    // GET /api/users/copilot/export/jobs/{job_id}/download?redirect=false
    getCopilotExportJobDownload: builder.query({
      query: ({ jobId }) => ({
        url: `/users/copilot/export/jobs/${encodeURIComponent(jobId)}/download?redirect=false`,
        cache: "no-cache",
      }),
    }),

    // ── Async export jobs (budget) — only the job-start endpoint differs from
    // usage above; status polling and download both go through the same
    // /users/copilot/export/jobs/{job_id} endpoints regardless of export type.

    // POST /api/users/copilot/budget/export/jobs?org_id=|cost_center_id=&from_date=&to_date=&platform_id=
    startCopilotBudgetExportJob: builder.mutation({
      query: ({ orgId, costCenterId, platformId, fromDate, toDate }) => {
        let url = "/users/copilot/budget/export/jobs?";
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}`;
        return { url, method: "POST" };
      },
    }),

    // Org-wide (or cost-center-scoped) paginated list of users at/near their
    // budget limit. Budget records are upserted and keyed by user only (one
    // running-total document per user, no period boundary) — so this
    // deliberately takes no date range. Exactly one of org_id/cost_center_id,
    // same rule as the other user-scoped endpoints above.
    // GET /api/users/copilot/budget/summary?org_id=|cost_center_id=&platform_id=&page=&per_page=&search=
    getCopilotBudgetSummary: builder.query({
      query: ({ orgId, costCenterId, platformId, page = 1, perPage = 20, search = "" }) => {
        let url = "/users/copilot/budget/summary?";
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&platform_id=${encodeURIComponent(platformId)}&page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url };
      },
    }),

    // Export per-user budget data as Excel (.xlsx). Filename comes from the
    // response's Content-Disposition header, so the raw response is captured
    // (not just the blob) to read that header before handing back the file.
    // Exactly one of org_id/cost_center_id, same rule as getCopilotExport above.
    // GET /api/users/copilot/budget/export?org_id=|cost_center_id=&from_date=&to_date=&platform_id=
    getCopilotBudgetExport: builder.query({
      query: ({ orgId, costCenterId, platformId, fromDate, toDate }) => {
        let url = "/users/copilot/budget/export?";
        url += costCenterId ? `cost_center_id=${encodeURIComponent(costCenterId)}` : `org_id=${encodeURIComponent(orgId)}`;
        url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}`;
        return {
          url,
          responseHandler: async (response) => ({
            blob: await response.blob(),
            filename: parseFilenameFromContentDisposition(response.headers.get("content-disposition")),
          }),
          cache: "no-cache",
        };
      },
    }),

    // API 5 — Org-wide usage aggregated by cost center, pre-sorted desc by total_gross_credits.
    // GET /api/users/copilot/usage/by-cost-center?org_id=&from_date=&to_date=&platform_id=
    getCopilotCostCenters: builder.query({
      query: ({ orgId, platformId, fromDate, toDate }) => ({
        url: `/users/copilot/usage/by-cost-center?org_id=${encodeURIComponent(orgId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&platform_id=${encodeURIComponent(platformId)}`,
      }),
      transformResponse: (responseData) => responseData.cost_centers ?? [],
    }),

    // API 6 — Usage grouped by cost center, sourced from per-user data (not the
    // org-level pre-aggregated collection). Exactly one of org_id/cost_center_id
    // is sent, same rule as every other user-scoped endpoint above: call with
    // org_id to list every cost-center group within that org (population the
    // selector's dropdown), or with cost_center_id to get that one cost center's
    // true totals across every org (the selected group's scoped summary).
    // GET /api/users/copilot-usage/cost-centers?org_id=|cost_center_id=&platform_id=&start_date=&end_date=
    getCopilotUsageByCostCenter: builder.query({
      query: ({ orgId, costCenterId, platformId, startDate, endDate }) => {
        let url = `/users/copilot-usage/cost-centers?platform_id=${encodeURIComponent(platformId)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
        url += costCenterId ? `&cost_center_id=${encodeURIComponent(costCenterId)}` : `&org_id=${encodeURIComponent(orgId)}`;
        return { url };
      },
    }),

    // ── Enterprise endpoints — mirror the org/cost-center-scoped ones above but
    // never take org_id or cost_center_id; they aggregate across every org on
    // the platform. Same response shapes as their scoped counterparts, so the
    // same components can render either by branching on which hook they call.

    // Mirrors getCopilotUsers. GET /api/users/copilot/enterprise/usage?platform_id=&from_date=&to_date=&search=&sort_by=&sort_order=&page=&per_page=
    getEnterpriseCopilotUsers: builder.query({
      query: ({ platformId, fromDate, toDate, search = "", sortBy = "total_consumed_credits", sortOrder = "desc", page = 1, perPage = 20 }) => {
        let url = `/users/copilot/enterprise/usage?platform_id=${encodeURIComponent(platformId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}&page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url };
      },
    }),

    // Mirrors getCopilotModelUsers. GET /api/users/copilot/enterprise/models/users?model_name=&platform_id=&from_date=&to_date=&search=&sort_by=&sort_order=&page=&per_page=
    getEnterpriseCopilotModelUsers: builder.query({
      query: ({ modelName, platformId, fromDate, toDate, search = "", sortBy = "gross_quantity", sortOrder = "desc", page = 1, perPage = 20 }) => {
        let url = `/users/copilot/enterprise/models/users?model_name=${encodeURIComponent(modelName)}&platform_id=${encodeURIComponent(platformId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}&page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url };
      },
    }),

    // Mirrors getCopilotBudgetSummary. No date range — budget is all-time.
    // GET /api/users/copilot/enterprise/budget/summary?platform_id=&page=&per_page=&search=
    getEnterpriseCopilotBudgetSummary: builder.query({
      query: ({ platformId, page = 1, perPage = 20, search = "" }) => {
        let url = `/users/copilot/enterprise/budget/summary?platform_id=${encodeURIComponent(platformId)}&page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url };
      },
    }),

    // Mirrors getCopilotUsageByCostCenter — note start_date/end_date, not
    // from_date/to_date, matching the org-scoped version of this endpoint.
    // GET /api/users/copilot/enterprise/usage/by-cost-center?platform_id=&start_date=&end_date=
    getEnterpriseCopilotUsageByCostCenter: builder.query({
      query: ({ platformId, startDate, endDate }) => ({
        url: `/users/copilot/enterprise/usage/by-cost-center?platform_id=${encodeURIComponent(platformId)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      }),
    }),

    // Mirrors getCopilotExport. GET /api/users/copilot/enterprise/export?platform_id=&from_date=&to_date=
    getEnterpriseCopilotExport: builder.query({
      query: ({ platformId, fromDate, toDate }) => ({
        url: `/users/copilot/enterprise/export?platform_id=${encodeURIComponent(platformId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
    }),

    // Mirrors getCopilotBudgetExport. GET /api/users/copilot/enterprise/budget/export?platform_id=&from_date=&to_date=
    getEnterpriseCopilotBudgetExport: builder.query({
      query: ({ platformId, fromDate, toDate }) => ({
        url: `/users/copilot/enterprise/budget/export?platform_id=${encodeURIComponent(platformId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
        responseHandler: async (response) => ({
          blob: await response.blob(),
          filename: parseFilenameFromContentDisposition(response.headers.get("content-disposition")),
        }),
        cache: "no-cache",
      }),
    }),

    // ── Enterprise async export jobs — mirror startCopilotExportJob/
    // startCopilotBudgetExportJob above but enterprise-wide (platform_id only,
    // no org_id/cost_center_id). Status polling and download reuse the same
    // getCopilotExportJobStatus/getCopilotExportJobDownload endpoints above —
    // job status/download aren't scoped by how the job was started.

    // POST /api/users/copilot/enterprise/export/jobs?platform_id=&from_date=&to_date=
    startEnterpriseCopilotExportJob: builder.mutation({
      query: ({ platformId, fromDate, toDate }) => ({
        url: `/users/copilot/enterprise/export/jobs?platform_id=${encodeURIComponent(platformId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
        method: "POST",
      }),
    }),

    // POST /api/users/copilot/enterprise/budget/export/jobs?platform_id=&from_date=&to_date=
    startEnterpriseCopilotBudgetExportJob: builder.mutation({
      query: ({ platformId, fromDate, toDate }) => ({
        url: `/users/copilot/enterprise/budget/export/jobs?platform_id=${encodeURIComponent(platformId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
        method: "POST",
      }),
    }),

    // Global dashboard settings — one shared config for all users, no org/user scoping.
    // GET /api/users/copilot/dashboard/settings
    getCopilotDashboardSettings: builder.query({
      query: () => ({
        url: `/users/copilot/dashboard/settings`,
      }),
    }),

    // PUT /api/users/copilot/dashboard/settings
    updateCopilotDashboardSettings: builder.mutation({
      query: (body) => ({
        url: `/users/copilot/dashboard/settings`,
        method: "PUT",
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          copilotApiQueries.util.updateQueryData("getCopilotDashboardSettings", undefined, () => body)
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            copilotApiQueries.util.updateQueryData("getCopilotDashboardSettings", undefined, () => data)
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetCopilotUsageQuery,
  useGetCopilotUsageMonthlyQuery,
  useGetCopilotInfoQuery,
  useGetCopilotUsersQuery,
  useLazyGetCopilotUsersQuery,
  useGetCopilotUserDetailQuery,
  useLazyGetCopilotUserDetailQuery,
  useGetCopilotModelUsersQuery,
  useLazyGetCopilotModelUsersQuery,
  useLazyGetCopilotExportQuery,
  useLazyGetCopilotBudgetExportQuery,
  useStartCopilotExportJobMutation,
  useLazyGetCopilotExportJobStatusQuery,
  useLazyGetCopilotExportJobDownloadQuery,
  useStartCopilotBudgetExportJobMutation,
  useGetCopilotBudgetSummaryQuery,
  useGetCopilotCostCentersQuery,
  useGetCopilotUsageByCostCenterQuery,
  useGetCopilotDashboardSettingsQuery,
  useUpdateCopilotDashboardSettingsMutation,
  useGetEnterpriseCopilotUsersQuery,
  useLazyGetEnterpriseCopilotUsersQuery,
  useGetEnterpriseCopilotModelUsersQuery,
  useLazyGetEnterpriseCopilotModelUsersQuery,
  useGetEnterpriseCopilotBudgetSummaryQuery,
  useGetEnterpriseCopilotUsageByCostCenterQuery,
  useLazyGetEnterpriseCopilotExportQuery,
  useLazyGetEnterpriseCopilotBudgetExportQuery,
  useStartEnterpriseCopilotExportJobMutation,
  useStartEnterpriseCopilotBudgetExportJobMutation,
} = copilotApiQueries;
